import React, { useState, useEffect, useCallback, useContext } from "react";
import { useNavigate } from "react-router-dom";

import { v4 as uuidv4 } from "uuid";
import axios from "axios";
import { getToken, URL } from "../../utils";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import NumberInput from "../../components/NumberInput";
import Select from "react-select";
import { customSelectStyles } from "../../utils/selectStyles";
import { UserContext } from "../../contexts/UserContext";
import { usePermissions } from "../../hooks/usePermissions";

const AddCard = () => {
  const { user } = useContext(UserContext);
  const isCaissierWithStore = user?.is_caissier && user?.magasin;

  const [formData, setFormData] = useState({
    articles: [{ stock_item: "", quantite: 1, unite: "unite", id: uuidv4() }],
    adresse_facturation: "",
    client_professionnel: null,
    client_particulier: null,
    magasin_id: 1,
    commercial_id: null,
    on_gozem: false,
    lieu_conso: "",
    type_client: "",
    commentaire: "",
    type_paiement: "",
    remise: 0,
    etat_paiement: "",
    montant_paye: 0,
  });

  const [clientsParticuliers, setClientsParticuliers] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [clientsProfessionnels, setClientsProfessionnels] = useState([]);
  const [magasins, setMagasins] = useState([]);
  const [selectedMagasinId, setSelectedMagasinId] = useState(null);
  const [articlesMagasin, setArticlesMagasin] = useState([]);
  const [commercials, setCommercials] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { hasPermission } = usePermissions();

  // État pour le modal d'erreur
  const [errorModal, setErrorModal] = useState({
    isOpen: false,
    title: "",
    message: "",
    type: "error", // "error", "auth", "validation"
    suggestions: [],
  });

  const handleClientChange = (event, value) => {
    if (formData.type_client === "client_pro") {
      setFormData((prev) => ({
        ...prev,
        client_professionnel: value?.id || "",
      }));
    } else if (formData.type_client === "client_particulier") {
      setFormData((prev) => ({ ...prev, client_particulier: value?.id || "" }));
    }
  };

  const clientOptions =
    formData.type_client === "client_pro"
      ? clientsProfessionnels.map((client) => ({
          id: client.id,
          nom: client.nom_entreprise,
          contact: client.contact,
          label: client.nom_entreprise,
        }))
      : clientsParticuliers.map((client) => ({
          id: client.id,
          nom: client.nom,
          prenom: client.prenom,
          contact: client.contact1,
          label: `${client.nom} ${client.prenom}`,
        }));

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      let next = { ...prev, [name]: value };
      if (name === "type_client") {
        if (value === "client_particulier") {
          next = {
            ...next,
            client_professionnel: null,
            etat_paiement: "paye",
            montant_paye: 0,
          };
        } else if (value === "client_pro") {
          next = {
            ...next,
            client_particulier: null,
            etat_paiement: "",
            montant_paye: 0,
          };
        } else {
          next = {
            ...next,
            client_professionnel: null,
            client_particulier: null,
            etat_paiement: "",
            montant_paye: 0,
          };
        }
      }
      return next;
    });
  };

  const handleArticleChange = (id, field, value) => {
    const updatedArticles = formData.articles.map((article) =>
      article.id === id ? { ...article, [field]: value } : article,
    );
    setFormData((prev) => ({ ...prev, articles: updatedArticles }));
  };

  const addArticle = () => {
    setFormData((prev) => ({
      ...prev,
      articles: [
        ...prev.articles,
        { stock_item: "", quantite: 1, unite: "unite", id: uuidv4() },
      ],
    }));
  };

  const deleteArticle = (id) => {
    const updatedArticles = formData.articles.filter(
      (article) => article.id !== id,
    );
    setFormData((prev) => ({ ...prev, articles: updatedArticles }));
  };

  const token = getToken();

  const navigate = useNavigate();

  // Fonction pour récupérer la liste des magasins (similaire au DegustationModal)
  const fetchMagasins = useCallback(async () => {
    try {
      const [sPResponse] = await Promise.all([
        axios.get(`${URL}Magasins/magasins-list`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }),
      ]);

      const magasinsOptions = sPResponse.data.map((magasin) => ({
        value: magasin.id,
        label: magasin.nom,
      }));

      setMagasins(magasinsOptions);
    } catch (error) {
      console.error("Erreur lors du chargement des magasins:", error);
      toast.error("Erreur lors du chargement des magasins");
    }
  }, [token]);

  useEffect(() => {
    // Si l'utilisateur n'a pas de magasin assigné, charger la liste des magasins
    if (!isCaissierWithStore && hasPermission("canViewAllStores")) {
      fetchMagasins();
    }
  }, [fetchMagasins, hasPermission, isCaissierWithStore]);

  useEffect(() => {
    const fetchCommercials = async () => {
      try {
        const response = await axios.get(
          `${URL}Magasins/commercial/liste-commerciaux`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          },
        );
        setCommercials(response.data);
      } catch (error) {
        toast.error("Erreur lors du chargement des commerciaux");
      }
    };

    if (token) {
      fetchCommercials();
    }
  }, [token]);

  useEffect(() => {
    // Utiliser user.magasin s'il est disponible, sinon selectedMagasinId
    const currentMagasinId = user?.magasin || selectedMagasinId;
    if (currentMagasinId) {
      setFormData((prev) => ({ ...prev, magasin_id: currentMagasinId }));
    }
  }, [user?.magasin, selectedMagasinId]);

  useEffect(() => {
    const fetchArticlesMagasin = async () => {
      // Utiliser user.magasin s'il est disponible, sinon selectedMagasinId
      const currentMagasinId = user?.magasin || selectedMagasinId;
      if (!currentMagasinId) return;

      try {
        const response = await axios.get(
          URL + `Magasins/articles-magasin/${currentMagasinId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        setArticlesMagasin(response.data.stock_items);
      } catch (err) {
        const errorMessage =
          err.response?.data?.message ||
          err.message ||
          "Erreur lors de la récupération des articles";
        toast.error(errorMessage);
      }
    };

    fetchArticlesMagasin();
  }, [user?.magasin, selectedMagasinId, token]);

  useEffect(() => {
    const fetchClientsParticuliers = async () => {
      try {
        const response = await axios.get(
          URL + `Clients/clients-particuliers-list-all`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        setClientsParticuliers(response.data);
      } catch (error) {
        console.error(
          "Erreur lors de la récupération des clients particuliers:",
          error,
        );
      }
    };

    if (token) {
      fetchClientsParticuliers();
    }
  }, [token]);

  useEffect(() => {
    const fetchClientsProfessionnels = async () => {
      try {
        const response = await axios.get(
          URL + `Clients/clients-professionnels-list-all`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        setClientsProfessionnels(response.data);
      } catch (error) {
        console.error(
          "Erreur lors de la récupération des clients professionnels:",
          error,
        );
      }
    };

    if (token) {
      fetchClientsProfessionnels();
    }
  }, [token]);

  const isFormValid = () => {
    // Vérifier qu'un magasin est sélectionné (soit assigné à l'utilisateur, soit sélectionné manuellement)
    const currentMagasinId = user?.magasin || selectedMagasinId;
    if (!currentMagasinId) {
      return false;
    }

    // Vérifie les champs obligatoires
    if (
      !formData.adresse_facturation ||
      !formData.type_client ||
      !formData.lieu_conso ||
      !formData.commercial_id ||
      !formData.type_paiement ||
      (formData.type_client === "client_pro" &&
        !formData.client_professionnel) ||
      (formData.type_client === "client_particulier" &&
        !formData.client_particulier)
    ) {
      return false;
    }

    // Vérification pour les clients professionnels
    if (formData.type_client === "client_pro") {
      // État de paiement est obligatoire pour les professionnels
      if (!formData.etat_paiement) {
        return false;
      }

      // Si l'état de paiement est "partiel", le montant payé est obligatoire
      if (formData.etat_paiement === "partiel" && !formData.montant_paye) {
        return false;
      }
    }

    // Vérifie que chaque article a les champs requis
    if (
      formData.articles.length === 0 ||
      formData.articles.some(
        (article) => !article.stock_item || !article.quantite || !article.unite,
      )
    ) {
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      // Vérifier qu'un magasin est sélectionné
      const currentMagasinId = user?.magasin || selectedMagasinId;
      if (!currentMagasinId) {
        toast.warning("Veuillez sélectionner un magasin");
        setIsSubmitting(false);
        return;
      }

      // Vérification du stock
      const stockErrors = formData.articles.filter((article) => {
        const stockItem = articlesMagasin.find(
          (item) => item.id === article.stock_item,
        );
        return stockItem && article.quantite > stockItem.stock_disponible;
      });

      if (stockErrors.length > 0) {
        const errorMessages = stockErrors
          .map((article) => {
            const stockItem = articlesMagasin.find(
              (item) => item.id === article.stock_item,
            );
            return `${article.quantite} ${stockItem.nom} (${stockItem.stock_disponible} disponibles)`;
          })
          .join(", ");

        toast.error(`Stock insuffisant pour : ${errorMessages}`, {
          autoClose: 5000,
          hideProgressBar: false,
        });
        setIsSubmitting(false);
        return;
      }

      // Validation supplémentaire côté front pour les états de paiement
      if (formData.type_client === "client_pro") {
        const allowedEtat = ["paye", "non_paye", "partiel"];
        if (!allowedEtat.includes(formData.etat_paiement)) {
          toast.error("État de paiement invalide");
          setIsSubmitting(false);
          return;
        }
      }

      // Construire le payload en omettant les champs non applicables
      const basePayload = {
        articles: formData.articles.map(({ stock_item, quantite, unite }) => ({
          stock_item: parseInt(stock_item),
          quantite: parseInt(quantite),
          unite,
        })),
        adresse_facturation: formData.adresse_facturation,
        client_professionnel: formData.client_professionnel
          ? parseInt(formData.client_professionnel)
          : null,
        client_particulier: formData.client_particulier
          ? parseInt(formData.client_particulier)
          : null,
        magasin_id: parseInt(currentMagasinId),
        commercial_id: formData.commercial_id
          ? parseInt(formData.commercial_id)
          : null,
        on_gozem: formData.on_gozem,
        lieu_conso: formData.lieu_conso,
        commentaire: formData.commentaire || "",
        type_paiement: formData.type_paiement,
        remise: formData.remise ? parseInt(formData.remise) : 0,
      };

      const payload = {
        ...basePayload,
        ...(formData.type_client === "client_pro"
          ? { etat_paiement: formData.etat_paiement }
          : { etat_paiement: "paye" }),
        ...(formData.type_client === "client_pro" &&
        formData.etat_paiement === "partiel"
          ? { montant_paye: parseInt(formData.montant_paye) || 0 }
          : {}),
      };

      console.log("Payload envoyé:", payload);

      const response = await axios.post(
        URL + `Declarations/ventes/vente-stock`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );
      console.log("Response:", response);
      if (response.status === 200 || response.status === 201) {
        toast.success("Facture enregistrée avec succès", {
          autoClose: 5000,
          hideProgressBar: false,
        });
        navigate("/sales");
      }
    } catch (err) {
      console.error("Erreur lors de la soumission:", err);

      const status = err.response?.status;
      const errorData = err.response?.data;

      // Gestion selon le code de statut HTTP
      if (status === 401 || status === 403) {
        setErrorModal({
          isOpen: true,
          title: status === 401 ? "Session expirée" : "Accès non autorisé",
          message:
            status === 401
              ? "Votre session a expiré ou vous n'êtes plus connecté."
              : "Vous n'avez pas les permissions nécessaires pour effectuer cette action.",
          type: "auth",
          suggestions: [
            "Rechargez la page pour actualiser votre session",
            "Vérifiez que vous êtes toujours connecté",
            "Reconnectez-vous si nécessaire",
          ],
        });
      } else if (status === 400) {
        // Erreur de validation / Bad Request
        const errorMessage =
          errorData?.error ||
          errorData?.message ||
          "Les données envoyées sont invalides";

        setErrorModal({
          isOpen: true,
          title: "Erreur de validation",
          message: errorMessage,
          type: "validation",
          suggestions: [
            "Vérifiez que tous les champs obligatoires sont remplis",
            "Assurez-vous que le type de paiement est valide (espèces, carte, mobile_money, virement)",
            "Vérifiez que les quantités sont correctes",
          ],
        });
      } else if (status === 404) {
        setErrorModal({
          isOpen: true,
          title: "Ressource introuvable",
          message:
            "L'élément que vous essayez d'accéder n'existe pas ou a été supprimé.",
          type: "error",
          suggestions: [
            "Vérifiez que le client sélectionné existe toujours",
            "Vérifiez que les articles sont toujours disponibles",
            "Rechargez la page et réessayez",
          ],
        });
      } else if (status >= 500) {
        setErrorModal({
          isOpen: true,
          title: "Erreur serveur",
          message:
            "Une erreur s'est produite sur le serveur. Veuillez réessayer plus tard.",
          type: "error",
          suggestions: [
            "Attendez quelques instants et réessayez",
            "Si le problème persiste, contactez le support technique",
          ],
        });
      } else {
        // Erreur réseau ou autre
        const errorMessage =
          errorData?.error ||
          errorData?.message ||
          err.message ||
          "Une erreur inattendue s'est produite";

        setErrorModal({
          isOpen: true,
          title: "Erreur",
          message: errorMessage,
          type: "error",
          suggestions: [
            "Vérifiez votre connexion internet",
            "Rechargez la page et réessayez",
          ],
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  // Fonction pour fermer le modal d'erreur
  const closeErrorModal = () => {
    setErrorModal((prev) => ({ ...prev, isOpen: false }));
  };

  // Fonction pour recharger la page
  const handleReloadPage = () => {
    window.location.reload();
  };

  // Fonction pour rediriger vers la page de connexion
  const handleGoToLogin = () => {
    navigate("/login");
  };

  return (
    <>
      {/* Modal d'erreur personnalisé */}
      {errorModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden animate-in fade-in zoom-in duration-200">
            {/* Header du modal */}
            <div
              className={`p-6 ${
                errorModal.type === "auth"
                  ? "bg-gradient-to-r from-orange-500 to-amber-500"
                  : errorModal.type === "validation"
                    ? "bg-gradient-to-r from-yellow-500 to-orange-500"
                    : "bg-gradient-to-r from-red-500 to-rose-600"
              }`}
            >
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  {errorModal.type === "auth" ? (
                    <svg
                      className="w-8 h-8 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                      />
                    </svg>
                  ) : errorModal.type === "validation" ? (
                    <svg
                      className="w-8 h-8 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                  ) : (
                    <svg
                      className="w-8 h-8 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  )}
                </div>
                <h3 className="ml-3 text-xl font-bold text-white">
                  {errorModal.title}
                </h3>
              </div>
            </div>

            {/* Corps du modal */}
            <div className="p-6">
              <p className="text-gray-700 text-base mb-4">
                {errorModal.message}
              </p>

              {errorModal.suggestions.length > 0 && (
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-sm font-semibold text-gray-600 mb-2">
                    Suggestions :
                  </p>
                  <ul className="space-y-2">
                    {errorModal.suggestions.map((suggestion, index) => (
                      <li
                        key={index}
                        className="text-sm text-gray-600 flex items-start"
                      >
                        <span className="text-red-500 mr-2">•</span>
                        {suggestion}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Footer du modal avec boutons d'action */}
            <div className="px-6 pb-6 flex flex-col sm:flex-row gap-3">
              {errorModal.type === "auth" ? (
                <>
                  <button
                    onClick={handleReloadPage}
                    className="flex-1 bg-gradient-to-r from-red-500 to-red-500 hover:from-red-600 hover:to-red-600 text-white py-3 px-4 rounded-xl font-semibold transition-all duration-200 flex items-center justify-center"
                  >
                    <svg
                      className="w-5 h-5 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                      />
                    </svg>
                    Recharger la page
                  </button>
                  <button
                    onClick={handleGoToLogin}
                    className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 px-4 rounded-xl font-semibold transition-all duration-200"
                  >
                    Se reconnecter
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={closeErrorModal}
                    className="flex-1 bg-gradient-to-r from-red-500 to-red-500 hover:from-red-600 hover:to-red-600 text-white py-3 px-4 rounded-xl font-semibold transition-all duration-200"
                  >
                    Compris
                  </button>
                  <button
                    onClick={handleReloadPage}
                    className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 px-4 rounded-xl font-semibold transition-all duration-200"
                  >
                    Recharger la page
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white shadow-2xl rounded-3xl overflow-hidden space-y-4">
            {/* Header avec gradient */}
            <div className="bg-gradient-to-r from-amber-500 to-orange-600 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-white mb-1">
                    Créer une facture
                  </h1>
                  <p className="text-amber-100">
                    Remplissez les informations ci-dessous pour créer une
                    nouvelle facture
                  </p>
                </div>
                <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3">
                  <span className="text-white text-sm font-medium">
                    {user?.username || "Chargement..."}
                  </span>
                </div>
              </div>
            </div>

            {/* Contenu principal */}
            <div className="p-8">
              <div className="space-y-8">
                {/* Sélection du magasin - seulement si l'utilisateur n'a pas de magasin assigné */}
                {!isCaissierWithStore && hasPermission("canViewAllStores") && (
                  // Iformation du magasin
                  <div className="space-y-8">
                    <div className="space-y-6">
                      <h2 className="text-2xl font-semibold text-gray-800 mb-6 flex items-center">
                        <div className="w-2 h-8 bg-gradient-to-b from-amber-500 to-orange-600 rounded-full mr-3"></div>
                        Informations magasin
                      </h2>

                      <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-6 shadow-lg">
                        <div className="flex items-center mb-4">
                          <div className="w-3 h-3 bg-amber-500 rounded-full mr-2"></div>
                          <label className="text-lg font-semibold text-gray-800">
                            Sélectionner un magasin *
                          </label>
                        </div>
                        <Select
                          value={
                            magasins.find(
                              (m) => m.value === selectedMagasinId,
                            ) || null
                          }
                          onChange={(selected) =>
                            setSelectedMagasinId(
                              selected ? selected.value : null,
                            )
                          }
                          options={magasins}
                          placeholder="Sélectionner un magasin"
                          isSearchable
                          className="text-sm shadow-md"
                          styles={{
                            ...customSelectStyles,
                            control: (provided) => ({
                              ...provided,
                              borderRadius: "12px",
                              border: "2px solid #f3f4f6",
                              boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                              "&:hover": {
                                border: "2px solid #f59e0b",
                              },
                            }),
                          }}
                          noOptionsMessage={() => "Aucun magasin disponible"}
                        />
                        {!selectedMagasinId && (
                          <p className="text-sm text-amber-700 mt-2 flex items-center">
                            <svg
                              className="w-4 h-4 mr-1"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                                clipRule="evenodd"
                              />
                            </svg>
                            Veuillez sélectionner un magasin pour continuer
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}{" "}
                {/* Section Articles */}
                <div>
                  <h2 className="text-2xl font-semibold text-gray-800 mb-6 flex items-center">
                    <div className="w-2 h-8 bg-gradient-to-b from-blue-500 to-indigo-600 rounded-full mr-3"></div>
                    Articles de la facture
                  </h2>
                  <div className="space-y-4">
                    {formData.articles.map((article, index) => (
                      <div
                        key={article.id}
                        className="bg-gray-50 border border-gray-200 rounded-2xl p-6 shadow-sm hover:shadow-md transition-all duration-200"
                      >
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-semibold text-gray-800">
                            Article #{index + 1}
                          </h3>
                          {formData.articles.length > 1 && (
                            <button
                              onClick={() => deleteArticle(article.id)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded-lg transition-all duration-200"
                              title="Supprimer cet article"
                            >
                              <svg
                                className="w-5 h-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                />
                              </svg>
                            </button>
                          )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          {/* Article */}
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              Article *
                            </label>
                            <Select
                              value={
                                articlesMagasin.find(
                                  (item) => item.id === parseInt(article.stock_item)
                                )
                                  ? {
                                      value: article.stock_item,
                                      label: articlesMagasin.find(
                                        (item) => item.id === parseInt(article.stock_item)
                                      )?.nom,
                                    }
                                  : null
                              }
                              onChange={(selected) =>
                                handleArticleChange(
                                  article.id,
                                  "stock_item",
                                  selected ? selected.value : "",
                                )
                              }
                              options={articlesMagasin.map((stockItem) => ({
                                value: stockItem.id,
                                label: stockItem.nom,
                              }))}
                              placeholder={
                                articlesMagasin.length === 0
                                  ? "Chargement des articles..."
                                  : "Sélectionner un article"
                              }
                              isSearchable
                              isClearable
                              className="text-sm"
                              styles={{
                                ...customSelectStyles,
                                control: (provided) => ({
                                  ...provided,
                                  borderRadius: "12px",
                                  border: "2px solid #e5e7eb",
                                  padding: "4px",
                                  boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                                  "&:hover": {
                                    border: "2px solid #3b82f6",
                                    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                                  },
                                }),
                              }}
                              noOptionsMessage={() => "Aucun article disponible"}
                            />
                          </div>

                          {/* Quantité */}
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              Quantité *
                            </label>
                            <NumberInput
                              value={article.quantite}
                              onChange={(value) =>
                                handleArticleChange(
                                  article.id,
                                  "quantite",
                                  value,
                                )
                              }
                              className="w-full py-3 px-4 border-2 border-gray-200 rounded-xl bg-white text-gray-900 focus:outline-none focus:border-blue-500 transition-colors duration-200 shadow-sm hover:shadow-md"
                              placeholder="Quantité"
                              inputMode="numeric"
                            />
                          </div>

                          {/* Unité */}
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              Unité
                            </label>
                            <select
                              value="unite"
                              disabled
                              className="w-full py-3 px-4 border-2 border-gray-200 rounded-xl bg-gray-100 text-gray-600 cursor-not-allowed"
                            >
                              <option value="unite">Unité</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    ))}

                    <button
                      onClick={addArticle}
                      className="w-full py-4 border-2 border-dashed border-gray-300 rounded-2xl text-gray-600 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 transition-all duration-200 flex items-center justify-center group"
                    >
                      <svg
                        className="w-6 h-6 mr-2 group-hover:scale-110 transition-transform duration-200"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                        />
                      </svg>
                      <span className="font-semibold">Ajouter un article</span>
                    </button>
                  </div>
                </div>
                {/* Section Informations Client */}
                <div className="space-y-6">
                  <h2 className="text-2xl font-semibold text-gray-800 mb-6 flex items-center">
                    <div className="w-2 h-8 bg-gradient-to-b from-teal-500 to-green-600 rounded-full mr-3"></div>
                    Informations Client
                  </h2>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Adresse de facturation */}
                    <div className="lg:col-span-2">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Adresse de facturation *
                      </label>
                      <input
                        type="text"
                        name="adresse_facturation"
                        value={formData.adresse_facturation}
                        onChange={handleChange}
                        className="w-full py-3 px-4 border-2 border-gray-200 rounded-xl bg-white text-gray-900 focus:outline-none focus:border-amber-500 transition-colors duration-200 shadow-sm hover:shadow-md"
                        placeholder="Entrez l'adresse de facturation"
                      />
                    </div>

                    {/* Commercial */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Commercial *
                      </label>
                      <Select
                        value={
                          commercials.find(
                            (c) => c.id === parseInt(formData.commercial_id)
                          )
                            ? {
                                value: formData.commercial_id,
                                label: `${commercials.find((c) => c.id === parseInt(formData.commercial_id))?.prenom} ${commercials.find((c) => c.id === parseInt(formData.commercial_id))?.nom}`,
                              }
                            : null
                        }
                        onChange={(selected) =>
                          setFormData((prev) => ({
                            ...prev,
                            commercial_id: selected ? selected.value : null,
                          }))
                        }
                        options={commercials.map((commercial) => ({
                          value: commercial.id,
                          label: `${commercial.prenom} ${commercial.nom}`,
                        }))}
                        placeholder="Sélectionner un commercial"
                        isSearchable
                        isClearable
                        className="text-sm"
                        styles={{
                          ...customSelectStyles,
                          control: (provided) => ({
                            ...provided,
                            borderRadius: "12px",
                            border: "2px solid #e5e7eb",
                            padding: "4px",
                            boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                            "&:hover": {
                              border: "2px solid #f59e0b",
                              boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                            },
                          }),
                        }}
                        noOptionsMessage={() => "Aucun commercial disponible"}
                      />
                    </div>

                    {/* Type de client */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Type de client *
                      </label>
                      <select
                        name="type_client"
                        value={formData.type_client}
                        onChange={handleChange}
                        className="w-full py-3 px-4 border-2 border-gray-200 rounded-xl bg-white text-gray-900 focus:outline-none focus:border-amber-500 transition-colors duration-200 shadow-sm hover:shadow-md"
                      >
                        <option value="">Sélectionner</option>
                        <option value="client_pro">Client professionnel</option>
                        <option value="client_particulier">
                          Client particulier
                        </option>
                      </select>
                    </div>

                    {/* Type de paiement */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Type de paiement *
                      </label>
                      <select
                        name="type_paiement"
                        value={formData.type_paiement}
                        onChange={handleChange}
                        className="w-full py-3 px-4 border-2 border-gray-200 rounded-xl bg-white text-gray-900 focus:outline-none focus:border-amber-500 transition-colors duration-200 shadow-sm hover:shadow-md"
                      >
                        <option value="">Sélectionner</option>
                        {/* 
                        [
                          "espece",
                          "mobile_money",
                          "carte_bancaire",
                          "cheque",
                          "virement_bancaire",
                          "autre_mode"
                        ]*/}
                        <option value="espece">Espèces</option>
                        <option value="mobile_money">Mobile Money</option>
                        <option value="carte_bancaire">Carte bancaire</option>
                        <option value="cheque">Chèque</option>
                        <option value="virement_bancaire">
                          Virement bancaire
                        </option>
                        <option value="autre_mode">Autre mode</option>
                      </select>
                    </div>

                    {/* Nom du client */}
                    <div
                      className={
                        formData.type_client === "client_pro" &&
                        formData.etat_paiement === "partiel"
                          ? ""
                          : "lg:col-span-2"
                      }
                    >
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Nom du client *
                      </label>
                      <div className="flex items-center gap-3">
                        <div className="flex-1">
                          <Autocomplete
                            disablePortal
                            options={clientOptions}
                            onChange={handleClientChange}
                            value={
                              formData.type_client === "client_pro"
                                ? clientOptions.find(
                                    (option) =>
                                      option.id ===
                                      formData.client_professionnel,
                                  ) || null
                                : clientOptions.find(
                                    (option) =>
                                      option.id === formData.client_particulier,
                                  ) || null
                            }
                            disabled={!formData.type_client}
                            title={
                              !formData.type_client
                                ? "Veuillez sélectionner un type de client d'abord"
                                : ""
                            }
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "12px",
                                backgroundColor: "white",
                                border: "2px solid #e5e7eb",
                                fontSize: "14px",
                                "&:hover": {
                                  border: "2px solid #f59e0b",
                                },
                                "&.Mui-focused": {
                                  border: "2px solid #f59e0b",
                                  boxShadow:
                                    "0 0 0 3px rgba(245, 158, 11, 0.1)",
                                },
                              },
                              "& .MuiInputLabel-root": {
                                color: "#6b7280",
                                "&.Mui-focused": {
                                  color: "#f59e0b",
                                },
                              },
                            }}
                            getOptionLabel={(option) =>
                              formData.type_client === "client_pro"
                                ? option.nom
                                : `${option.nom} ${option.prenom}`
                            }
                            renderOption={(props, option) => (
                              <li
                                {...props}
                                className="flex flex-col py-2 px-3 hover:bg-amber-50 rounded-lg"
                              >
                                <span className="font-medium text-gray-900">
                                  {formData.type_client === "client_pro"
                                    ? option.nom
                                    : `${option.nom} ${option.prenom}`}
                                </span>
                                <span className="text-sm text-gray-500">
                                  Contact : {option.contact}
                                </span>
                              </li>
                            )}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Nom du client"
                                placeholder="Rechercher un client..."
                                size="medium"
                              />
                            )}
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => setOpenDialog(true)}
                          className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 whitespace-nowrap"
                        >
                          <svg
                            className="w-5 h-5 inline-block"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>

                    {/* Lieu de consommation */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Lieu de consommation *
                      </label>
                      <select
                        name="lieu_conso"
                        value={formData.lieu_conso}
                        onChange={handleChange}
                        className="w-full py-3 px-4 border-2 border-gray-200 rounded-xl bg-white text-gray-900 focus:outline-none focus:border-amber-500 transition-colors duration-200 shadow-sm hover:shadow-md"
                      >
                        <option value="">Sélectionner</option>
                        <option value="on_place">Sur place</option>
                        <option value="emporte">À emporter</option>
                      </select>
                    </div>

                    {/* Achat via Gozem */}
                    <div className="lg:col-span-2">
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        Achat via Gozem
                      </label>
                      <div className="flex items-center gap-6">
                        <label className="flex items-center cursor-pointer group">
                          <div className="relative">
                            <input
                              type="radio"
                              name="on_gozem"
                              value={true}
                              checked={formData.on_gozem === true}
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  on_gozem: true,
                                }))
                              }
                              className="sr-only"
                            />
                            <div
                              className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
                                formData.on_gozem === true
                                  ? "border-amber-500 bg-amber-500"
                                  : "border-gray-300 group-hover:border-amber-400"
                              }`}
                            >
                              {formData.on_gozem === true && (
                                <div className="w-2 h-2 rounded-full bg-white"></div>
                              )}
                            </div>
                          </div>
                          <span className="ml-3 text-sm font-medium text-gray-700">
                            Oui
                          </span>
                        </label>

                        <label className="flex items-center cursor-pointer group">
                          <div className="relative">
                            <input
                              type="radio"
                              name="on_gozem"
                              value={false}
                              checked={formData.on_gozem === false}
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  on_gozem: false,
                                }))
                              }
                              className="sr-only"
                            />
                            <div
                              className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
                                formData.on_gozem === false
                                  ? "border-amber-500 bg-amber-500"
                                  : "border-gray-300 group-hover:border-amber-400"
                              }`}
                            >
                              {formData.on_gozem === false && (
                                <div className="w-2 h-2 rounded-full bg-white"></div>
                              )}
                            </div>
                          </div>
                          <span className="ml-3 text-sm font-medium text-gray-700">
                            Non
                          </span>
                        </label>
                      </div>
                    </div>

                    {/* État de paiement - uniquement pour les professionnels */}
                    {formData.type_client === "client_pro" && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          État de paiement *
                        </label>
                        <select
                          name="etat_paiement"
                          value={formData.etat_paiement}
                          onChange={handleChange}
                          className="w-full py-3 px-4 border-2 border-gray-200 rounded-xl bg-white text-gray-900 focus:outline-none focus:border-amber-500 transition-colors duration-200 shadow-sm hover:shadow-md"
                        >
                          <option value="">Sélectionner</option>
                          <option value="paye">Payé</option>
                          <option value="non_paye">Non payé</option>
                          <option value="partiel">Partiel</option>
                        </select>
                      </div>
                    )}

                    {/* Montant payé - uniquement si état de paiement est "partiel" */}
                    {formData.type_client === "client_pro" &&
                      formData.etat_paiement === "partiel" && (
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Montant payé *
                          </label>
                          <NumberInput
                            value={formData.montant_paye}
                            onChange={(value) =>
                              setFormData((prev) => ({
                                ...prev,
                                montant_paye: value,
                              }))
                            }
                            className="w-full py-3 px-4 border-2 border-gray-200 rounded-xl bg-white text-gray-900 focus:outline-none focus:border-amber-500 transition-colors duration-200 shadow-sm hover:shadow-md"
                            placeholder="Entrez le montant payé"
                            inputMode="numeric"
                          />
                        </div>
                      )}

                    {/* Remise */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Remise (optionnel)
                      </label>
                      <NumberInput
                        value={formData.remise}
                        onChange={(value) =>
                          setFormData((prev) => ({
                            ...prev,
                            remise: value,
                          }))
                        }
                        className="w-full py-3 px-4 border-2 border-gray-200 rounded-xl bg-white text-gray-900 focus:outline-none focus:border-amber-500 transition-colors duration-200 shadow-sm hover:shadow-md"
                        placeholder="Montant de la remise"
                        inputMode="numeric"
                      />
                    </div>
                  </div>

                  {/* Section Commentaire */}
                  <div>
                    <h2 className="text-2xl font-semibold text-gray-800 mb-6 flex items-center">
                      <div className="w-2 h-8 bg-gradient-to-b from-purple-500 to-pink-600 rounded-full mr-3"></div>
                      Commentaire (optionnel)
                    </h2>
                    <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6">
                      <textarea
                        name="commentaire"
                        value={formData.commentaire}
                        onChange={handleChange}
                        placeholder="Ajoutez un commentaire ou des notes spéciales pour cette facture..."
                        className="w-full py-3 px-4 border-2 border-gray-200 rounded-xl bg-white text-gray-900 focus:outline-none focus:border-purple-500 transition-colors duration-200 resize-none h-28 shadow-sm hover:shadow-md"
                      />
                    </div>
                  </div>

                  {/* Boutons d'action */}
                  <div className="flex flex-col sm:flex-row gap-4 pt-8 border-t border-gray-200">
                    <button
                      onClick={() => navigate("/sales")}
                      className="flex-1 py-4 px-6 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-all duration-200 border-2 border-transparent hover:border-gray-300"
                    >
                      <svg
                        className="w-5 h-5 inline-block mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                      Annuler
                    </button>
                    <button
                      onClick={handleSubmit}
                      disabled={!isFormValid() || isSubmitting}
                      title={
                        !isFormValid()
                          ? "Veuillez remplir tous les champs obligatoires"
                          : isSubmitting
                            ? "Création en cours..."
                            : ""
                      }
                      className={`flex-1 py-4 px-6 rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center justify-center ${
                        isFormValid() && !isSubmitting
                          ? "bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
                          : "bg-gray-300 text-gray-500 cursor-not-allowed transform-none hover:shadow-lg"
                      }`}
                    >
                      {isSubmitting ? (
                        <>
                          <svg
                            className="animate-spin -ml-1 mr-3 h-5 w-5 text-current"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Création en cours...
                        </>
                      ) : (
                        <>
                          <svg
                            className="w-5 h-5 inline-block mr-2"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                          Enregistrer la facture
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal de dialogue */}
            {openDialog && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
                <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full transform transition-all duration-200 scale-100">
                  <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-6 rounded-t-2xl">
                    <h2 className="text-2xl font-bold text-white text-center">
                      Nouveau Client
                    </h2>
                    <p className="text-blue-100 text-center mt-1">
                      Choisissez le type de client à ajouter
                    </p>
                  </div>
                  <div className="p-6 space-y-4">
                    <button
                      onClick={() => {
                        setOpenDialog(false);
                        navigate("/sales/nouveau-client-pro");
                      }}
                      className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white py-4 px-6 rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center justify-center"
                    >
                      <svg
                        className="w-6 h-6 mr-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                        />
                      </svg>
                      Client Professionnel
                    </button>
                    <button
                      onClick={() => {
                        setOpenDialog(false);
                        navigate("/sales/nouveau-client-particulier");
                      }}
                      className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white py-4 px-6 rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center justify-center"
                    >
                      <svg
                        className="w-6 h-6 mr-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                        />
                      </svg>
                      Client Particulier
                    </button>
                    <button
                      onClick={() => setOpenDialog(false)}
                      className="w-full text-gray-500 hover:text-gray-700 py-3 font-medium hover:bg-gray-50 rounded-xl transition-all duration-200"
                    >
                      Annuler
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AddCard;
